import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from "react";
// THE SAME RENDERER THE LEGACY REDLINE REVIEW USES (components/RedlineReview.tsx).
//
// The owner pointed at it: "Why don't you take a look at the legacy code that
// appears in drafting to see if it handles redlining any better?" On the exact
// point he was complaining about, it does — it diffs word by word. This screen
// struck the WHOLE clause and inserted the WHOLE clause, so a change amending six
// words of a paragraph read as "delete this provision, write another one". That
// is what he saw and called "deletions and rewrites of the same exact language".
//
// The generated Word document was already surgical — the Python engine diffs the
// span before writing revisions, and its largest deletion on this contract is 86
// characters. So the screen was misrepresenting the very document it previews.
import { diffWords } from "diff";
import type { ActivityStreamState } from "../../shared/activityStream";
import { CoreComposer } from "./CoreComposer";
import { CoreIcon } from "./CoreIcon";
import { ThreadActivityPanel } from "./ThreadActivityPanel";
import type {
  CoreComposerProps,
  DraftChange,
  DraftChangeStatus,
  DraftClause,
  DraftExportChanges,
  DraftExportFormat,
  DraftExportRequest,
  DraftVersion,
  MaybePromise,
} from "./types";

export type DraftFormatCommand =
  | "bold" | "italic" | "underline" | "strike"
  | "bulleted-list" | "numbered-list" | "link" | "undo" | "redo";

/**
 * Layout note. Both draft screens are fixed-frame: the document scrolls inside its
 * own pane and the instructions box stays pinned to the bottom of the conversation
 * column. That only works if the screen fills a frame of known height, which
 * `.di-redesign` now supplies (see the comment on it in document-intelligence.css —
 * it holds the whole story, including why a loaded draft used to have no chat box).
 * Nothing here needs to measure anything.
 */

/** Paragraphs of an imported draft rendered in the preview pane before it is truncated. */
export const DRAFT_PREVIEW_PARAGRAPH_LIMIT = 1200;


export interface BlankDraftScreenProps {
  composer: CoreComposerProps;
  examples?: string[];
  accepting?: string;
  busy?: boolean;
  selectedDraft?: { name: string; source?: string; previewText?: string } | null;
  /** Live narration of a running draft review. While present it REPLACES the
   * intro/examples column — the author just asked for the work, so the screen's
   * job is to show the work happening, stage by stage, not to keep advertising
   * examples next to a spinner. */
  activity?: ActivityStreamState | null;
  onImportDraft?: (file: File) => MaybePromise;
  onChooseExistingDraft?: () => MaybePromise;
  onExampleSelect?: (instruction: string) => MaybePromise;
  onFormatCommand?: (command: DraftFormatCommand) => void;
  onParagraphStyleChange?: (style: string) => void;
}

export function BlankDraftScreen({
  composer,
  examples = [
    "Redline the definition of confidential information to require that material be marked or designated in writing.",
    "Review this draft against a playbook and apply our standard positions.",
    "Draft a mutual CDA using our approved template.",
  ],
  accepting = ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  busy = false,
  selectedDraft = null,
  activity = null,
  onImportDraft,
  onChooseExistingDraft,
  onExampleSelect,
  onFormatCommand,
  onParagraphStyleChange,
}: BlankDraftScreenProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file && onImportDraft) void onImportDraft(file);
  };

  const commands: Array<{ command: DraftFormatCommand; label: string; text: string }> = [
    { command: "bold", label: "Bold", text: "B" },
    { command: "italic", label: "Italic", text: "I" },
    { command: "underline", label: "Underline", text: "U" },
    { command: "strike", label: "Strikethrough", text: "S" },
    { command: "bulleted-list", label: "Bulleted list", text: "•" },
    { command: "numbered-list", label: "Numbered list", text: "1." },
    { command: "link", label: "Insert link", text: "↗" },
    { command: "undo", label: "Undo", text: "↶" },
    { command: "redo", label: "Redo", text: "↷" },
  ];

  const previewParagraphs = selectedDraft?.previewText
    ? selectedDraft.previewText.split(/\n{2,}/).filter(Boolean)
    : [];
  const previewTruncated = previewParagraphs.length > DRAFT_PREVIEW_PARAGRAPH_LIMIT;

  return (
    <main className="di-core-screen di-core-blank-draft">
      <section className="di-core-blank-draft__conversation" aria-label="Draft instructions">
        {activity ? (
          <div className="di-core-blank-draft__activity" aria-label="Draft review progress">
            <p className="di-core-kicker">Working on your draft</p>
            <ThreadActivityPanel state={activity} connection="open" showWorking={false} />
          </div>
        ) : (
        <div className="di-core-blank-draft__intro">
          <p className="di-core-kicker">New draft</p>
          <h1>Import a document, or start from nothing</h1>
          <p>Attach the document you want to work on and tell DI what to do with it. Changes come back as tracked edits, each with its rationale.</p>
          <p className="di-core-overline">For example</p>
          <div className="di-core-example-list">
            {examples.map((example) => (
              <button
                type="button"
                key={example}
                disabled={!onExampleSelect || busy}
                title={!onExampleSelect ? "Draft instructions are not available." : undefined}
                onClick={() => void onExampleSelect?.(example)}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
        )}
        <div className="di-core-blank-draft__composer">
          <p className="di-core-overline">{selectedDraft ? `Instructions for ${selectedDraft.name}` : "Instructions"}</p>
          <CoreComposer
            {...composer}
            compact
            placeholder={composer.placeholder || "Describe what you want done with this draft…"}
            submitLabel={composer.submitLabel || "Send"}
          />
        </div>
      </section>
      <section className="di-core-blank-draft__editor" aria-label="Blank document editor">
        <div className="di-core-editor-toolbar" role="toolbar" aria-label="Document formatting">
          <label>
            <span className="di-core-sr-only">Paragraph style</span>
            <select
              defaultValue="paragraph"
              disabled={!onParagraphStyleChange}
              title={!onParagraphStyleChange ? "Formatting becomes available after a draft is loaded." : undefined}
              onChange={(event) => onParagraphStyleChange?.(event.currentTarget.value)}
            >
              <option value="paragraph">Paragraph</option>
              <option value="heading-1">Heading 1</option>
              <option value="heading-2">Heading 2</option>
            </select>
          </label>
          {commands.map((item) => (
            <button
              key={item.command}
              type="button"
              aria-label={item.label}
              disabled={!onFormatCommand}
              title={!onFormatCommand ? "Formatting becomes available after a draft is loaded." : item.label}
              onClick={() => onFormatCommand?.(item.command)}
            >
              {item.text}
            </button>
          ))}
          <span className="di-core-spacer" />
          <button type="button" disabled title="There are no tracked changes in a blank draft.">Show edits</button>
        </div>
        <div className={`di-core-blank-canvas ${selectedDraft?.previewText ? "di-core-blank-canvas--preview" : ""}`}>
          <div>
            <p>{selectedDraft ? "Draft ready for instructions." : "Write something, import a draft, or choose one already in KontractAI."}</p>
            {selectedDraft ? (
              <div className="di-core-imported-draft" role="status">
                <CoreIcon name="file" />
                <span><strong>{selectedDraft.name}</strong><small>{selectedDraft.source || "Selected draft"}</small></span>
              </div>
            ) : null}
            {selectedDraft?.previewText ? (
              <article className="di-core-draft-preview" aria-label={`${selectedDraft.name} preview`}>
                {previewParagraphs.slice(0, DRAFT_PREVIEW_PARAGRAPH_LIMIT).map((paragraph, index) => (
                  <p key={`${index}:${paragraph.slice(0, 24)}`}>{paragraph}</p>
                ))}
                {previewTruncated ? (
                  <p className="di-core-draft-preview__truncated">
                    Preview stops at the first {DRAFT_PREVIEW_PARAGRAPH_LIMIT} paragraphs. The whole document is still used
                    when Document Intelligence works on it.
                  </p>
                ) : null}
              </article>
            ) : null}
            {/*
              The "Import draft" and "Choose from KontractAI" buttons were removed here
              on owner instruction: "'Import draft' and 'choose from KontractAI' on the
              right side of the screen should be removed, and the user can add files
              through the files button instead."

              Everything they did is in the Files menu on the instruction box below —
              browse this computer, add from the project, and the KontractAI repository.
              The repository option was wired into THIS screen's Files menu before these
              buttons were deleted; it had previously existed only on the assistant
              screen, so removing them first would have quietly dropped the capability.

              "Import draft" also took a single file and ignored the rest. The Files
              menu accepts several at once and adds to what is already attached.
            */}
          </div>
        </div>
      </section>
    </main>
  );
}

export interface VersionHistoryDialogProps {
  open: boolean;
  versions: DraftVersion[];
  workingVersion: number;
  busyVersion?: number | null;
  onClose: () => void;
  onRestore?: (version: DraftVersion) => MaybePromise;
}

export function VersionHistoryDialog({
  open,
  versions,
  workingVersion,
  busyVersion,
  onClose,
  onRestore,
}: VersionHistoryDialogProps) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose, open]);
  if (!open) return null;
  return (
    <div className="di-core-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="di-core-dialog di-core-dialog--versions" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="di-core-dialog__header">
          <h2 id={titleId}>Version history</h2>
          <button type="button" className="di-core-btn di-core-btn--secondary di-core-btn--icon" aria-label="Close version history" onClick={onClose}>
            <CoreIcon name="close" />
          </button>
        </header>
        <div className="di-core-version-list">
          {[...versions].sort((a, b) => b.number - a.number).map((version) => (
            <article key={version.id} className={version.number === workingVersion ? "is-current" : ""}>
              <strong>v{version.number}</strong>
              <div><p>{version.summary}</p><small>{version.byline}</small></div>
              {version.number === workingVersion ? (
                <span className="di-core-tag di-core-tag--accent">Working version</span>
              ) : (
                <button
                  type="button"
                  className="di-core-btn di-core-btn--secondary"
                  disabled={!onRestore || busyVersion !== null && busyVersion !== undefined}
                  title={!onRestore ? "Version restore is not available." : undefined}
                  onClick={() => void onRestore?.(version)}
                >
                  {busyVersion === version.number ? "Restoring…" : "Restore"}
                </button>
              )}
            </article>
          ))}
        </div>
        <footer className="di-core-dialog__footer">
          <span>Restoring makes that version the working document. Nothing is deleted.</span>
          <span className="di-core-spacer" />
          <button type="button" className="di-core-btn di-core-btn--primary" onClick={onClose}>Done</button>
        </footer>
      </section>
    </div>
  );
}

export interface ExportWordDialogProps {
  open: boolean;
  formats: DraftExportFormat[];
  initialFormatId?: string;
  workingVersion: number;
  openChangeCount: number;
  /**
   * Whether the TRACKED export also needs every change decided.
   *
   * THE DIALOG MUST NOT PROMISE WHAT THE SERVER BEHIND IT WILL REFUSE, and there
   * are two servers behind it. The rebuilt engine
   * (`redlineV2/reviewService.js`) refuses only the clean modes when changes are
   * pending — a tracked document carries them as proposals, which is the point
   * of tracked changes. The original engine (`docintRedliner.js`) refuses ANY
   * generation with an undecided change, with no mode distinction at all, and
   * the screen falls back to it whenever the rebuilt engine is unavailable —
   * including wherever `REDLINE_V2_ENABLED` is off, which is every estate by
   * default because flags land off.
   *
   * Defaulting to `true` is deliberate: an unknown engine is treated as the
   * stricter one, so the failure is a button that asks for a decision it did not
   * need, not a button that promises an export and then 409s.
   */
  trackedNeedsDecisions?: boolean;
  filename: string;
  busy?: boolean;
  onClose: () => void;
  onExport?: (request: DraftExportRequest) => MaybePromise<{ filename?: string } | void>;
  onDownloadAgain?: () => MaybePromise;
}

export function ExportWordDialog({
  open,
  formats,
  initialFormatId,
  workingVersion,
  openChangeCount,
  trackedNeedsDecisions = true,
  filename,
  busy = false,
  onClose,
  onExport,
  onDownloadAgain,
}: ExportWordDialogProps) {
  const titleId = useId();
  const [phase, setPhase] = useState<"form" | "done">("form");
  const [formatId, setFormatId] = useState(initialFormatId || formats[0]?.id || "");
  const [changes, setChanges] = useState<DraftExportChanges>("tracked");
  const [exportedFilename, setExportedFilename] = useState(filename);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // THE DIALOG RESETS WHEN IT OPENS. NOT WHILE IT IS OPEN.
  //
  // THIS IS THE DEFECT THE OWNER ACTUALLY HIT, and it is why nothing he tried
  // worked: "The export is still not available for either option Even after I
  // accepted all revisions."
  //
  // The dependency list was `[filename, formats, initialFormatId, open]`.
  // `formats` arrives from the shell as an INLINE ARRAY LITERAL, so it is a new
  // reference on every parent render. Every parent render therefore re-ran this
  // effect while the dialog was open and:
  //   - set `changes` back to "tracked", discarding the option he had chosen;
  //   - set `error` back to "", discarding the server's explanation.
  //
  // Reproduced in a browser on dev: choosing "export clean", clicking Export,
  // and receiving a 422 the user never saw — the radio had already snapped back
  // to "tracked" and the error line was gone before it could paint. From the
  // outside that is a button that does nothing, twice, which is exactly what he
  // reported and exactly what no unit test was asking about.
  //
  // The reset now fires on the OPEN TRANSITION only. `wasOpen` is a ref rather
  // than a dependency so the values it reads cannot themselves retrigger it —
  // making the identity of `formats` irrelevant here instead of load-bearing.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPhase("form");
      setFormatId(initialFormatId || formats[0]?.id || "");
      setChanges("tracked");
      setExportedFilename(filename);
      setSubmitting(false);
      setError("");
    }
    wasOpen.current = open;
  }, [filename, formats, initialFormatId, open]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose, open]);

  if (!open) return null;
  // ONE EXPRESSION FOR "THE SERVER WILL REFUSE THIS", used by the button, its
  // tooltip and the explanatory line. Three copies of the same condition is how
  // a control comes to disagree with the sentence beside it.
  const blocked = openChangeCount > 0 && (changes === "clean" || trackedNeedsDecisions);
  const formatName = formats.find((format) => format.id === formatId)?.name || "Plain · no template";
  const exportDocument = async () => {
    if (!onExport) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await onExport({ formatId, changes, workingVersion });
      if (result && "filename" in result && result.filename) setExportedFilename(result.filename);
      setPhase("done");
    } catch (reason) {
      // A failed export used to reject unhandled — the button just stopped
      // saying "Exporting…" and the dialog said nothing at all (owner:
      // "Export to Word does not work"). The server's own sentence renders here.
      setError(reason instanceof Error && reason.message ? reason.message : "The export failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="di-core-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="di-core-dialog di-core-dialog--export" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        {phase === "form" ? (
          <>
            <header className="di-core-dialog__title"><h2 id={titleId}>Export to Word</h2></header>
            <div className="di-core-dialog__body">
              <label className="di-core-field">Export format
                <select value={formatId} onChange={(event) => setFormatId(event.currentTarget.value)}>
                  {formats.map((format) => <option key={format.id} value={format.id}>{format.name}</option>)}
                </select>
              </label>
              <fieldset className="di-core-fieldset">
                <legend>Changes</legend>
                {/* Each option maps to a REAL server artifact: "tracked" is the
                    redline with revision markup, "clean" is the accepted-text
                    document. The former third option ("comparison") had no
                    server counterpart and did nothing; a dead control teaches
                    the user the live ones are dead too, so it is gone.
                    The label text is WRAPPED IN A SPAN deliberately: the
                    stylesheet stretches `.di-core-radio > span:last-child` to
                    fill the row, and with bare text the DOT was the last span —
                    which rendered every radio as a row-wide ellipse. */}
                {([
                  ["tracked", "Keep as tracked changes for the counterparty to see"],
                  ["clean", "Apply my decisions and export clean, without markup"],
                ] as const).map(([id, label]) => (
                  <label key={id} className="di-core-radio">
                    <input type="radio" checked={changes === id} onChange={() => setChanges(id)} />
                    <span className="di-core-radio__dot" />
                    <span>{label}</span>
                  </label>
                ))}
              </fieldset>
              {/* THE GATE IS PER-OPTION, and it has to be.
                  A tracked-changes export carries the proposals AS proposals —
                  that is the whole point of a redline, and the server has
                  accepted one with open changes since #462. Only the CLEAN
                  export needs every change decided, because it has to write
                  final text and an undecided change has no final text.
                  Gating both options on the same counter is what left the owner
                  with an Export button that would not light up for either one. */}
              <p className="di-core-muted">
                {openChangeCount === 0
                  ? "All changes have been decided."
                  : blocked
                    ? changes === "clean"
                      ? `${openChangeCount} change${openChangeCount === 1 ? " still needs" : "s still need"} a decision before a clean copy can be written. Accept or reject ${openChangeCount === 1 ? "it" : "each one"} in the changes panel${trackedNeedsDecisions ? "." : ", or export as tracked changes instead."}`
                      : `${openChangeCount} change${openChangeCount === 1 ? " still needs" : "s still need"} a decision before this document can be generated. Accept or reject ${openChangeCount === 1 ? "it" : "each one"} in the changes panel.`
                    : `${openChangeCount} change${openChangeCount === 1 ? " is" : "s are"} still open. The tracked-changes export includes ${openChangeCount === 1 ? "it" : "them"} as a proposed revision the other side can accept or reject.`}
              </p>
              {error && <p className="di-core-dialog__error" role="alert">{error}</p>}
            </div>
            <footer className="di-core-dialog__footer di-core-dialog__footer--actions">
              <button type="button" className="di-core-btn di-core-btn--secondary" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="di-core-btn di-core-btn--primary"
                disabled={!onExport || !formatId || busy || submitting || blocked}
                title={!onExport
                  ? "Word export is not configured."
                  : blocked
                    ? changes === "clean"
                      ? `A clean copy needs every change decided.${trackedNeedsDecisions ? "" : " Accept or reject them, or export as tracked changes."}`
                      : "This document needs every change decided first."
                    : undefined}
                onClick={() => void exportDocument()}
              >
                {busy || submitting ? "Exporting…" : "Export"}
              </button>
            </footer>
          </>
        ) : (
          <>
            <header className="di-core-dialog__title"><h2 id={titleId}>Exported</h2></header>
            <div className="di-core-dialog__body">
              <div className="di-core-export-result">
                <CoreIcon name="file" />
                <strong>{exportedFilename}</strong>
                <span>{formatName}</span>
              </div>
              <p>Tracked changes preserved. Continue in Word for macros, add-ins or complex formatting.</p>
            </div>
            <footer className="di-core-dialog__footer di-core-dialog__footer--actions">
              <button type="button" className="di-core-btn di-core-btn--secondary" onClick={onClose}>Close</button>
              <button
                type="button"
                className="di-core-btn di-core-btn--primary"
                disabled={!onDownloadAgain}
                title={!onDownloadAgain ? "The exported file is not available for another download." : undefined}
                onClick={() => void onDownloadAgain?.()}
              >
                Download again
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

export interface LoadedDraftScreenProps {
  title: string;
  filename: string;
  documentMeta: string;
  // How many documents were attached as reference material for this review. Drives
  // the kicker, which previously claimed a playbook was used on every single review.
  referenceCount?: number;
  versions: DraftVersion[];
  workingVersion: number;
  clauses: DraftClause[];
  changes: DraftChange[];
  playbookName: string;
  playbookMet: number;
  playbookTotal: number;
  exportFormats: DraftExportFormat[];
  selectedExportFormatId?: string;
  verifyGate?: boolean;
  /** Whether a generated Word document exists for this review yet. Download is
   * honestly disabled until one does — it used to fail silently. */
  hasArtifacts?: boolean;
  /** Passed through to the export dialog. See `ExportWordDialogProps`: the two
   * redline engines disagree about whether a TRACKED export needs every change
   * decided, and the dialog must not offer what the server behind it refuses. */
  trackedNeedsDecisions?: boolean;
  /** Changes the engine drafted and could not place. Shown because a shortfall
   * nobody can see is indistinguishable from work that was never done: the
   * reviewer is entitled to know a point was raised and then lost, and to go
   * make the edit themselves. */
  droppedChanges?: Array<{
    id?: string;
    explanation?: string;
    replacement_text?: string | null;
    category?: string | null;
    rationale?: string | null;
  }>;
  /** The general-review memo (owner ruling Q-53). Present only when the review
   * ran WITHOUT a playbook: observations on market practice, each with
   * suggested language quoted for the attorney's adaptation — never applied to
   * the document. When set, this panel replaces the tracked-changes panel,
   * because a general review produces no changes by construction. */
  memo?: {
    label: string;
    observations: Array<{
      provision: string;
      observation: string;
      confidence: string;
      draft_language?: string | null;
      location_note?: string | null;
    }>;
    rejected_observations?: Array<{ provision: string; code?: string; reason: string }>;
  } | null;
  conversation?: Array<{ id?: string; role: string; content: string; created_at?: string }>;
  reviseComposer?: Omit<CoreComposerProps, "onSubmit">;
  onRestoreVersion?: (version: DraftVersion) => MaybePromise;
  onChangeDecision?: (change: DraftChange, decision: "accept" | "reject") => MaybePromise;
  /** Decide every open change in one server call. */
  onDecideAll?: (decision: "accepted" | "rejected") => MaybePromise;
  onExport?: (request: DraftExportRequest) => MaybePromise<{ filename?: string } | void>;
  onDownload?: () => MaybePromise;
  onDownloadExport?: () => MaybePromise;
  onExportFormatChange?: (formatId: string) => void;
  onRevise?: (prompt: string, mode: "edit" | "answer") => MaybePromise;
}

function clauseText(clause: DraftClause, replacement: boolean) {
  return `${clause.before}${replacement ? clause.replacement : clause.original}${clause.after}`;
}

/**
 * Word-level redline, the way a lawyer reads one.
 *
 * Ported from the legacy `RedlineReview.tsx` renderer at the owner's direction.
 * Unchanged words stay unmarked; only what actually moved is struck or inserted.
 * A change that amends six words of a paragraph now reads as six words amended,
 * not as the whole provision deleted and rewritten in identical language.
 *
 * Three deliberate choices:
 *  - `diffWords`, not `diffChars`. Character diffs split words mid-token and are
 *    unreadable on legal prose.
 *  - The <del>/<ins> ELEMENTS are kept rather than styled spans, so the existing
 *    stylesheet, the print path and assistive technology all keep working. The
 *    legacy screen used inline styles; this one already has semantics.
 *  - If the diff throws for any reason, fall back to the whole-clause strike and
 *    insert. Degraded but truthful beats a blank paragraph — the reader must
 *    never silently lose a proposed change.
 */
function renderWordDiff(original: string, replacement: string) {
  const from = original || "";
  const to = replacement || "";
  // Nothing to compare against: this is a pure insertion or a pure deletion.
  if (!from) return to ? <ins>{to}</ins> : null;
  if (!to) return <del>{from}</del>;
  try {
    return (
      <>
        {diffWords(from, to).map((part, index) => {
          if (part.added) return <ins key={index}>{part.value}</ins>;
          if (part.removed) return <del key={index}>{part.value}</del>;
          return <span key={index}>{part.value}</span>;
        })}
      </>
    );
  } catch {
    return <>{<del>{from}</del>}{<ins>{to}</ins>}</>;
  }
}

export function LoadedDraftScreen({
  title,
  filename,
  documentMeta,
  referenceCount = 0,
  versions,
  workingVersion,
  clauses,
  changes,
  playbookName,
  playbookMet,
  playbookTotal,
  exportFormats,
  selectedExportFormatId,
  verifyGate = true,
  hasArtifacts = false,
  trackedNeedsDecisions = true,
  droppedChanges = [],
  memo = null,
  conversation = [],
  reviseComposer,
  onRestoreVersion,
  onChangeDecision,
  onDecideAll,
  onExport,
  onDownload,
  onDownloadExport,
  onExportFormatChange,
  onRevise,
}: LoadedDraftScreenProps) {
  const maxVersion = Math.max(workingVersion, ...versions.map((version) => version.number));
  const [currentVersion, setCurrentVersion] = useState(workingVersion);
  const [showEdits, setShowEdits] = useState(true);
  // NEVER expanded by default. Owner-directed 2026-08-05: "any accept or
  // reject of revisions Must be displayed by default, not toggled" — the
  // changes panel with its Accept/Reject buttons is on screen from the first
  // paint, and expanding the document is the opt-in.
  const [expanded, setExpanded] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);
  const [decisionBusy, setDecisionBusy] = useState("");
  const [mode, setMode] = useState<"edit" | "answer">("edit");
  const [formatId, setFormatId] = useState(selectedExportFormatId || exportFormats[0]?.id || "");
  const [statuses, setStatuses] = useState<Record<string, DraftChangeStatus>>(
    Object.fromEntries(changes.map((change) => [change.id, change.status || "open"]))
  );

  useEffect(() => setCurrentVersion(workingVersion), [workingVersion]);
  useEffect(() => setFormatId(selectedExportFormatId || exportFormats[0]?.id || ""), [exportFormats, selectedExportFormatId]);
  useEffect(() => {
    // THE SERVER WINS WHEN IT HAS DECIDED. Optimistic local state wins only
    // while the server still says "open".
    //
    // This read `current[change.id] || change.status || "open"`, which preferred
    // whatever this component last put in its own map over a freshly fetched
    // server status. So a change decided anywhere other than this component's
    // own two handlers — a reload mid-flight, a decision made in another tab or
    // on another surface, an accept-all whose optimistic write did not run
    // because its await threw after the server had already committed — stayed
    // "open" on screen for the life of the session. It reads as a decision that
    // did not take, and it holds the export gate shut on a review the server
    // considers fully decided.
    //
    // The optimistic write is still preferred over a server "open" so a click
    // does not visibly bounce back while its request is in flight.
    setStatuses((current) => Object.fromEntries(changes.map((change) => {
      const fromServer = change.status || "open";
      return [change.id, fromServer === "open" ? (current[change.id] || "open") : fromServer];
    })));
  }, [changes]);

  const visibleChanges = useMemo(
    () => changes.filter((change) => change.introducedInVersion <= currentVersion),
    [changes, currentVersion]
  );
  const openCount = visibleChanges.filter((change) => statuses[change.id] === "open").length;
  const changeById = useMemo(() => new Map(changes.map((change) => [change.id, change])), [changes]);

  const restore = async (version: DraftVersion) => {
    if (!onRestoreVersion) return;
    setRestoringVersion(version.number);
    try {
      await onRestoreVersion(version);
      setCurrentVersion(version.number);
      setVersionDialogOpen(false);
    } finally {
      setRestoringVersion(null);
    }
  };

  const decide = async (change: DraftChange, decision: "accept" | "reject") => {
    if (!onChangeDecision) return;
    setDecisionBusy(change.id);
    try {
      await onChangeDecision(change, decision);
      setStatuses((current) => ({ ...current, [change.id]: decision === "accept" ? "accepted" : "rejected" }));
    } finally {
      setDecisionBusy("");
    }
  };

  // ONE server call, not a loop. The loop was the defect: every decision bumps
  // the review's revision, so the second call onward was refused with a
  // conflict and only one change was ever decided (owner, 2026-08-05).
  const acceptAllRemaining = async () => {
    if (!onDecideAll) return;
    setDecisionBusy("all");
    try {
      await onDecideAll("accepted");
      setStatuses((current) => {
        const next = { ...current };
        for (const change of visibleChanges) {
          if ((next[change.id] || "open") === "open") next[change.id] = "accepted";
        }
        return next;
      });
    } finally {
      setDecisionBusy("");
    }
  };

  return (
    <main className="di-core-screen di-core-loaded-draft">
      <div className="di-core-draft-toolbar" role="toolbar" aria-label="Draft tools">
        <div className="di-core-segmented" aria-label="Tracked change display">
          <button type="button" aria-pressed={showEdits} onClick={() => setShowEdits(true)}>Show edits</button>
          <button type="button" aria-pressed={!showEdits} onClick={() => setShowEdits(false)}>Final</button>
        </div>
        <button
          type="button"
          className="di-core-btn di-core-btn--secondary"
          aria-pressed={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show changes panel" : "Expand document"}
        </button>
        <button type="button" className="di-core-btn di-core-btn--secondary" onClick={() => setVersionDialogOpen(true)}>
          <CoreIcon name="clock" /> Version history · v{currentVersion} of {maxVersion}
        </button>
        <span className="di-core-spacer" />
        <label className="di-core-template-select">
          <span className="di-core-sr-only">Export template</span>
          <select value={formatId} onChange={(event) => {
            setFormatId(event.currentTarget.value);
            onExportFormatChange?.(event.currentTarget.value);
          }}>
            {exportFormats.map((format) => <option key={format.id} value={format.id}>Template: {format.name}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="di-core-btn di-core-btn--secondary"
          disabled={!onDownload || !hasArtifacts}
          title={!onDownload
            ? "Draft download is not available."
            : !hasArtifacts
              ? "No Word document has been generated yet — use Export to Word."
              : undefined}
          onClick={() => void onDownload?.()}
        >
          <CoreIcon name="download" /> Download
        </button>
        <button
          type="button"
          className="di-core-btn di-core-btn--primary"
          disabled={!onExport || !exportFormats.length}
          title={!onExport ? "Word export is not configured." : undefined}
          onClick={() => setExportDialogOpen(true)}
        >
          Export to Word
        </button>
      </div>

      {verifyGate && (
        <div className="di-core-verification" role="note">
          <CoreIcon name="warning" />
          <span>AI-generated draft. Verify accuracy, completeness, citations and legal judgement before it leaves the firm.</span>
        </div>
      )}

      <div className={`di-core-loaded-draft__body${expanded ? " di-core-loaded-draft__body--expanded" : ""}`}>
        <section className="di-core-document-viewport" aria-label="Draft document">
          <article className="di-core-paper">
            {/* NO SYNTHETIC HEADER. Owner-directed 2026-08-05: "This header should
                not appear. It should just be the actual draft of the uploaded
                document for editing."
                A kicker, a title and a change count are the tool talking about
                the document. What a lawyer opens this pane to see is the
                document. The same facts are already on the toolbar (the file
                name, the version) and in the changes panel (the count), so
                nothing is lost by taking them off the page itself. */}
            {clauses.map((clause, index) => {
              const change = clause.changeId ? changeById.get(clause.changeId) : undefined;
              const exists = Boolean(change && change.introducedInVersion <= currentVersion);
              const status = change ? statuses[change.id] : undefined;
              const previousHeading = index > 0 ? clauses[index - 1].heading : undefined;
              return (
                <div key={clause.id}>
                  {clause.heading && clause.heading !== previousHeading && <h2>{clause.heading}</h2>}
                  <p className="di-core-clause">
                    {/* Render the label only when the document actually has one.
                        An empty <strong> plus a space used to leave a stray indent;
                        an invented counter used to leave something worse. */}
                    {/* Do not print the label twice. In some documents a paragraph's
                        own text already begins with its clause label — Word stores it
                        as a literal run as well as applying it through numbering — and
                        rendering both makes it look like the numbering is still wrong,
                        which is exactly what the reader is checking for. */}
                    {clause.number && !String(clause.original || "").trimStart().startsWith(clause.number)
                      ? <><strong>{clause.number}</strong>{" "}</>
                      : null}
                    {!change || !exists || status === "rejected"
                      ? clauseText(clause, false)
                      : status === "accepted" || !showEdits
                        ? clauseText(clause, true)
                        : <>
                            {clause.before}
                            {renderWordDiff(clause.original, clause.replacement)}
                            {clause.after}
                          </>}
                  </p>
                </div>
              );
            })}
          </article>
        </section>

        {/* The general-review memo replaces the tracked-changes panel outright:
            a memo review has zero changes by construction, and rendering an
            empty changes list beside the memo would read as a review that
            found nothing rather than one that deliberately edits nothing. */}
        {!expanded && memo && (
          <aside className="di-core-changes" aria-label="Review memo">
            <header>
              <p className="di-core-kicker">Review memo</p>
              <span>{memo.observations.length} observation{memo.observations.length === 1 ? "" : "s"}</span>
            </header>
            <div className="di-core-dropped" role="note">
              <strong>{memo.label}</strong>
              <p>
                No playbook was attached, so this is a general review: observations reflect market
                practice as the model understands it, not your firm's positions. Nothing was applied
                to the document — suggested language is quoted for your own drafting.
              </p>
            </div>
            <div className="di-core-changes__scroll">
              {memo.observations.map((observation, index) => (
                <div key={index} className="di-core-dropped" role="note">
                  <strong>{observation.provision}</strong> <em>— {observation.confidence}</em>
                  <p>{observation.observation}</p>
                  {observation.draft_language
                    ? <p><em>Suggested language: “{observation.draft_language}”</em></p>
                    : null}
                  {observation.location_note ? <p><em>{observation.location_note}</em></p> : null}
                </div>
              ))}
              {(memo.rejected_observations || []).length > 0 && (
                <div className="di-core-dropped" role="note">
                  <strong>
                    {(memo.rejected_observations || []).length} observation{(memo.rejected_observations || []).length === 1 ? " was" : "s were"} refused by validation
                  </strong>
                  <ul>
                    {(memo.rejected_observations || []).map((rejectedObservation, index) => (
                      <li key={index}>{rejectedObservation.provision}: {rejectedObservation.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        )}
        {/* Not hidden by CSS — out of the tree entirely while expanded, so
            nothing off-screen can still catch focus or a screen reader. */}
        {!expanded && !memo && (
        <aside className="di-core-changes" aria-label="Tracked changes">
          <header><p className="di-core-kicker">Tracked changes</p><span>{openCount} of {visibleChanges.length} open</span></header>
          {openCount > 0 && onDecideAll && (
            <div className="di-core-changes__bulk">
              <button
                type="button"
                className="di-core-btn di-core-btn--secondary"
                disabled={Boolean(decisionBusy)}
                onClick={() => void acceptAllRemaining()}
              >
                {decisionBusy ? "Saving…" : `Accept all ${openCount} remaining`}
              </button>
            </div>
          )}
          <div className="di-core-changes__scroll">
            {/* INSIDE the scroll area, and COLLAPSED by default (owner-reported
                BCG regression, Finding 2). This block used to sit between the
                header and the scroll container: as a non-scrolling flex sibling
                it grew with its content, and on a review with eight verbose
                unplaced entries it consumed the panel's entire height — the
                decision cards existed below with nothing able to scroll to
                them, which read as "no accept, no reject, no scrolling". The
                disclosure stays one click away; the decisions stay reachable. */}
            {droppedChanges.length > 0 && (
              <details className="di-core-dropped" role="note">
                <summary>
                  <strong>
                    {droppedChanges.length} further change{droppedChanges.length === 1 ? " was" : "s were"} drafted but could not be placed — expand for each one
                  </strong>
                </summary>
                <p>Each one is a point the review raised. Nothing was applied for them — make these edits by hand if you want them.</p>
                <ul>
                  {droppedChanges.map((change, index) => (
                    <li key={change.id || index}>
                      {change.category ? <strong>{change.category}: </strong> : null}
                      {change.explanation}
                      {change.rationale ? <em>{change.rationale}</em> : null}
                      {change.replacement_text
                        ? <em>Proposed wording: “{change.replacement_text.slice(0, 300)}”</em>
                        : null}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {visibleChanges.length > 0 ? (
              <>
                <article className="di-core-coverage">
                  <p className="di-core-overline">Playbook coverage</p>
                  <strong>{playbookName}</strong>
                  <div><span><i style={{ width: `${playbookTotal ? Math.round((playbookMet / playbookTotal) * 100) : 0}%` }} /></span><small>{playbookMet} of {playbookTotal} met</small></div>
                  <p>{visibleChanges.length} required an edit · each is a card below.</p>
                </article>
                {visibleChanges.map((change) => {
                  const status = statuses[change.id] || "open";
                  if (status !== "open") {
                    return (
                      <div className="di-core-change-resolved" key={change.id}>
                        <CoreIcon name={status === "accepted" ? "check" : "x"} size={14} />
                        {change.position} · {change.clause} {status}
                      </div>
                    );
                  }
                  return (
                    <article className="di-core-change-card" key={change.id}>
                      <div>
                        <span className="di-core-tag di-core-tag--accent">{change.position} · {change.clause}</span>
                        {change.isComment && <span className="di-core-tag">Comment — no text change</span>}
                        <small>DI · {change.timestamp || "now"}</small>
                      </div>
                      <p>{change.rationale}</p>
                      <p className="di-core-muted">{change.grounding}</p>
                      <div className="di-core-actions">
                        <button
                          type="button"
                          className="di-core-btn di-core-btn--primary"
                          disabled={!onChangeDecision || Boolean(decisionBusy)}
                          title={!onChangeDecision ? "Change decisions are not available." : undefined}
                          onClick={() => void decide(change, "accept")}
                        >
                          {decisionBusy === change.id ? "Saving…" : "Accept"}
                        </button>
                        <button
                          type="button"
                          className="di-core-btn di-core-btn--secondary"
                          disabled={!onChangeDecision || Boolean(decisionBusy)}
                          title={!onChangeDecision ? "Change decisions are not available." : undefined}
                          onClick={() => void decide(change, "reject")}
                        >
                          Reject
                        </button>
                      </div>
                    </article>
                  );
                })}
                {/* This described a "comparison between version 1 and the latest
                    version" — a third export option that was removed because it
                    had no server counterpart and did nothing. The option went;
                    the sentence advertising it stayed. Copy that describes a
                    control which no longer exists is the same failure as a
                    control whose name does not describe what it catches. */}
                <p className="di-core-muted">Export to Word as tracked changes, or apply your decisions and export a clean copy.</p>
              </>
            ) : (
              <p className="di-core-clean-version">
                Version {currentVersion} is the counterparty draft as it arrived · no edits have been applied. Restore a later version, or ask DI below to review it again.
              </p>
            )}
          </div>
          {/*
            The instructions box is not conditional. A loaded draft that renders
            without one is a dead surface — the document is on screen and there is
            no way to ask for anything — so an omitted `reviseComposer` degrades to
            a disabled box that says why, rather than to no box at all.
          */}
          <footer className="di-core-changes__footer" aria-label="Draft instructions">
            <div className="di-core-segmented">
              <button type="button" aria-pressed={mode === "edit"} onClick={() => setMode("edit")}>Edit the draft</button>
              <button type="button" aria-pressed={mode === "answer"} onClick={() => setMode("answer")}>Answer in thread</button>
            </div>
            <p>{mode === "edit" ? "DI will apply the requested changes to the draft as tracked edits." : "DI will respond in the conversation without modifying the draft."}</p>
            {conversation.length > 0 && (
              <div className="di-core-draft-conversation" aria-label="Draft conversation">
                {conversation.slice(-6).map((message, index) => (
                  <div key={message.id || `${message.role}-${index}`} className={`is-${message.role}`}>
                    <strong>{message.role === "user" ? "You" : "Document Intelligence"}</strong>
                    <p>{message.content}</p>
                  </div>
                ))}
              </div>
            )}
            <CoreComposer
              {...(reviseComposer || {})}
              compact
              placeholder={reviseComposer?.placeholder || "Ask about this draft, or describe another revision…"}
              submitLabel={reviseComposer?.submitLabel || "Send"}
              onSubmit={onRevise ? (prompt) => onRevise(prompt, mode) : undefined}
            />
            {!onRevise && (
              <p className="di-core-muted">Draft instructions are not available for this document.</p>
            )}
          </footer>
        </aside>
        )}
      </div>

      <VersionHistoryDialog
        open={versionDialogOpen}
        versions={versions}
        workingVersion={currentVersion}
        busyVersion={restoringVersion}
        onClose={() => setVersionDialogOpen(false)}
        onRestore={onRestoreVersion ? restore : undefined}
      />
      <ExportWordDialog
        open={exportDialogOpen}
        formats={exportFormats}
        initialFormatId={formatId}
        workingVersion={currentVersion}
        openChangeCount={openCount}
        trackedNeedsDecisions={trackedNeedsDecisions}
        filename={filename}
        onClose={() => setExportDialogOpen(false)}
        onExport={onExport}
        onDownloadAgain={onDownloadExport}
      />
    </main>
  );
}
